import { useState } from 'react'
import { Lock, Unlock, Copy, Check } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function TextEncoder() {
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [operation, setOperation] = useState('encode')
  const [format, setFormat] = useState('base64')
  const [copied, setCopied] = useState(false)

  const processText = () => {
    if (!input.trim()) {
      alert('Please enter some text')
      return
    }

    try {
      let result = ''

      if (operation === 'encode') {
        switch (format) {
          case 'base64':
            result = btoa(unescape(encodeURIComponent(input)))
            break
          case 'url':
            result = encodeURIComponent(input)
            break
          case 'html':
            result = input
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;')
            break
          case 'hex':
            result = input.split('').map(char => 
              char.charCodeAt(0).toString(16).padStart(2, '0')
            ).join(' ')
            break
          case 'binary':
            result = input.split('').map(char => 
              char.charCodeAt(0).toString(2).padStart(8, '0')
            ).join(' ')
            break
          default:
            result = input
        }
      } else {
        switch (format) {
          case 'base64':
            result = decodeURIComponent(escape(atob(input)))
            break
          case 'url':
            result = decodeURIComponent(input)
            break
          case 'html':
            result = input
              .replace(/&amp;/g, '&')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
            break
          case 'hex':
            result = input.split(' ').map(hex => 
              String.fromCharCode(parseInt(hex, 16))
            ).join('')
            break
          case 'binary':
            result = input.split(' ').map(bin => 
              String.fromCharCode(parseInt(bin, 2))
            ).join('')
            break
          default:
            result = input
        }
      }

      setOutput(result)
    } catch (error) {
      alert('Error processing text: ' + error.message)
    }
  }

  const copyToClipboard = () => {
    if (output) {
      navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const clearAll = () => {
    setInput('')
    setOutput('')
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-3">Operation:</label>
              <select
                value={operation}
                onChange={(e) => setOperation(e.target.value)}
                className="input w-full"
              >
                <option value="encode">Encode</option>
                <option value="decode">Decode</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Format:</label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className="input w-full"
              >
                <option value="base64">Base64</option>
                <option value="url">URL Encoding</option>
                <option value="html">HTML Entities</option>
                <option value="hex">Hexadecimal</option>
                <option value="binary">Binary</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">
              {operation === 'encode' ? 'Enter text to encode:' : 'Enter text to decode:'}
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="input w-full min-h-[150px]"
              placeholder={operation === 'encode' ? 'Enter text to encode...' : 'Enter encoded text...'}
            />
          </div>

          <div className="flex gap-4">
            <Button onClick={processText} variant="primary" className="flex-1">
              {operation === 'encode' ? (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  Encode
                </>
              ) : (
                <>
                  <Unlock className="w-5 h-5 mr-2" />
                  Decode
                </>
              )}
            </Button>
            <Button onClick={clearAll} variant="outline">
              Clear
            </Button>
          </div>
        </Card.Body>
      </Card>

      {output && (
        <Card>
          <Card.Body>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-gray-700 font-semibold">Result:</label>
                <Button onClick={copyToClipboard} variant="outline" size="sm">
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <textarea
                value={output}
                readOnly
                className="input w-full min-h-[150px] bg-gray-50"
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {!output && (
        <Card>
          <Card.Body className="text-center py-12">
            <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Enter text and select encode/decode operation</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

